import type { ReactNode } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { StudioPage } from "./studio/StudioPage";
import { ApprovalsPage } from "./pages/admin/ApprovalsPage";
import { AuditPage } from "./pages/admin/AuditPage";
import { DeviceGroupsPage } from "./pages/admin/DeviceGroupsPage";
import { DevicesPage } from "./pages/admin/DevicesPage";
import { AiActivityPage } from "./pages/admin/AiActivityPage";
import { SystemHealthPage } from "./pages/admin/SystemHealthPage";
import { TenantsPage } from "./pages/admin/TenantsPage";
import { UsersPage } from "./pages/admin/UsersPage";
import { LoginPage } from "./pages/LoginPage";
import { ShareViewerPage } from "./pages/ShareViewerPage";
import { UnauthorizedPage } from "./pages/UnauthorizedPage";
import { ApiTokensPage } from "./pages/v2/ApiTokensPage";
import { DataPage } from "./pages/v2/DataPage";
import { MyFilesPage } from "./pages/v2/MyFilesPanel";
import { HomePage } from "./pages/v2/HomePage";
import { PowerBiImportPage } from "./pages/v2/PowerBiImportPage";
import { ReportsGalleryPage } from "./pages/v2/ReportsGalleryPage";
import { ReportViewerPage } from "./pages/v2/ReportViewerPage";
import { ReportWorkspacePage } from "./pages/v2/ReportWorkspacePage";
import { TemplatesPage } from "./pages/v2/TemplatesPage";
import { AppShell } from "./shell/AppShell";
import { ADMIN_ROLES, basePath, modeFor } from "./shell/role";

const OWNER_ROLES = [...ADMIN_ROLES, "report_owner"];
const ANY_ROLE = [...OWNER_ROLES, "report_viewer"];

function Shelled({ roles, children }: { roles?: string[]; children: ReactNode }) {
  return (
    <ProtectedRoute requiredRoles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/** Signed-in users landing on an unknown URL go to their own home, not the login page. */
function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const mode = modeFor(user);
  return <Navigate to={mode === "admin" ? "/admin" : `${basePath(mode)}/reports`} replace />;
}

function LegacyDatasetsRedirect() {
  const id = new URLSearchParams(window.location.search).get("id");
  return <Navigate to={id ? `/admin/data?table=${encodeURIComponent(id)}` : "/admin/data"} replace />;
}

function LegacyViewerRedirect() {
  const { reportId } = useParams();
  return <Navigate to={`/viewer/reports/${reportId}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Share link: public on purpose (no ProtectedRoute) -- the token is the credential. */}
      <Route path="/s/:token" element={<ShareViewerPage />} />
      <Route path="/unauthorized" element={<ProtectedRoute><UnauthorizedPage /></ProtectedRoute>} />

      {/* Admin workspace */}
      <Route path="/admin" element={<Shelled roles={ADMIN_ROLES}><HomePage /></Shelled>} />
      <Route path="/admin/reports" element={<Shelled roles={ADMIN_ROLES}><ReportsGalleryPage /></Shelled>} />
      <Route path="/admin/reports/:reportId" element={<Shelled roles={ADMIN_ROLES}><ReportWorkspacePage /></Shelled>} />
      <Route path="/admin/reports/:reportId/edit" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><StudioPage mode="admin" /></ProtectedRoute>} />
      <Route path="/admin/data" element={<Shelled roles={ADMIN_ROLES}><DataPage /></Shelled>} />
      {/* v2.2: "Data sources" + "Datasets" became one "Data" page; old links keep working. */}
      <Route path="/admin/data-sources" element={<Navigate to="/admin/data" replace />} />
      <Route path="/admin/datasets" element={<LegacyDatasetsRedirect />} />
      <Route path="/admin/devices" element={<Shelled roles={ADMIN_ROLES}><DevicesPage /></Shelled>} />
      <Route path="/admin/device-groups" element={<Shelled roles={ADMIN_ROLES}><DeviceGroupsPage /></Shelled>} />
      <Route path="/admin/users" element={<Shelled roles={ADMIN_ROLES}><UsersPage /></Shelled>} />
      <Route path="/admin/tenants" element={<Shelled roles={ADMIN_ROLES}><TenantsPage /></Shelled>} />
      <Route path="/admin/approvals" element={<Shelled roles={ADMIN_ROLES}><ApprovalsPage /></Shelled>} />
      <Route path="/admin/audit" element={<Shelled roles={ADMIN_ROLES}><AuditPage /></Shelled>} />
      <Route path="/admin/ai-activity" element={<Shelled roles={ADMIN_ROLES}><AiActivityPage /></Shelled>} />
      <Route path="/admin/system-health" element={<Shelled roles={ADMIN_ROLES}><SystemHealthPage /></Shelled>} />
      <Route path="/admin/import/powerbi" element={<Shelled roles={ADMIN_ROLES}><PowerBiImportPage /></Shelled>} />

      {/* Report owners (admins can use these too) */}
      <Route path="/owner" element={<Navigate to="/owner/reports" replace />} />
      <Route path="/owner/reports" element={<Shelled roles={OWNER_ROLES}><ReportsGalleryPage /></Shelled>} />
      <Route path="/owner/reports/:reportId" element={<Shelled roles={OWNER_ROLES}><ReportWorkspacePage /></Shelled>} />
      <Route path="/owner/reports/:reportId/edit" element={<ProtectedRoute requiredRoles={OWNER_ROLES}><StudioPage mode="owner" /></ProtectedRoute>} />

      {/* v3.1: My OneDrive & SharePoint (the Microsoft connect flow lands here; page hides itself when the feature is off) */}
      <Route path="/data" element={<Shelled roles={OWNER_ROLES}><MyFilesPage /></Shelled>} />

      {/* Report templates (owners and admins) */}
      <Route path="/templates" element={<Shelled roles={OWNER_ROLES}><TemplatesPage /></Shelled>} />

      {/* Viewers (everyone) */}
      <Route path="/viewer" element={<Navigate to="/viewer/reports" replace />} />
      <Route path="/viewer/reports" element={<Shelled roles={ANY_ROLE}><ReportsGalleryPage viewerOnly /></Shelled>} />
      <Route path="/viewer/reports/:reportId" element={<Shelled roles={ANY_ROLE}><ReportViewerPage /></Shelled>} />
      <Route path="/reports/:reportId/view" element={<LegacyViewerRedirect />} />

      {/* Personal settings */}
      <Route path="/settings/api-tokens" element={<Shelled roles={ANY_ROLE}><ApiTokensPage /></Shelled>} />

      <Route path="*" element={<RoleHome />} />
    </Routes>
  );
}
